import React, { useState } from "react";
import { Mic, Volume2, Loader2, RotateCcw, Flame, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";
import { playTTS } from "@/lib/audio";
import { useRecorder } from "@/hooks/useRecorder";
import { Waveform } from "@/components/Waveform";
import { useLang } from "@/context/LangContext";

const FLAME_COLORS = ["#f97316", "#ef4444", "#f59e0b", "#fb7185", "#fbbf24"];

function FlameBurst() {
  return (
    <div className="absolute inset-x-0 -top-2 bottom-0 flex justify-center pointer-events-none z-10">
      {Array.from({ length: 14 }).map((_, i) => (
        <span
          key={i}
          className="flame-particle"
          style={{
            left: `${18 + Math.random() * 64}%`,
            background: `radial-gradient(circle at 50% 70%, ${FLAME_COLORS[i % FLAME_COLORS.length]}, transparent 72%)`,
            animationDelay: `${Math.random() * 0.5}s`,
            width: `${12 + Math.random() * 12}px`,
            height: `${16 + Math.random() * 16}px`,
          }}
        />
      ))}
    </div>
  );
}

export default function SpeechCheck({ phrase, translation = "", testid = "speech-check" }) {
  const rec = useRecorder();
  const { t } = useLang();
  const [state, setState] = useState("idle"); // idle | listening | scoring | result
  const [result, setResult] = useState(null);
  const [burst, setBurst] = useState(false);

  if (!phrase) return null;

  const score = result?.overall ?? 0;
  const tier = score >= 85 ? "fire" : score >= 60 ? "good" : "retry";

  const toggle = async () => {
    if (state === "listening") {
      const res = await rec.stop();
      setState("scoring");
      if (!res?.blob) { setState("idle"); return; }
      try {
        const fd = new FormData();
        fd.append("file", res.blob, res.filename);
        fd.append("target", phrase);
        const { data } = await client.post("/pronounce", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setResult(data);
        setState("result");
        if ((data.overall ?? 0) >= 85) { setBurst(true); setTimeout(() => setBurst(false), 1700); }
      } catch { toast.error(t("couldnt_score")); setState("idle"); }
    } else {
      setResult(null);
      const ok = await rec.start();
      if (ok) setState("listening"); else toast.error(t("mic_permission"));
    }
  };

  const tierStyle = {
    fire: { ring: "from-orange-500 to-red-500", chip: "bg-orange-100 text-orange-700", msg: t("sc_excellent"), Icon: Flame },
    good: { ring: "from-emerald-500 to-teal-500", chip: "bg-emerald-100 text-emerald-700", msg: t("sc_good"), Icon: ThumbsUp },
    retry: { ring: "from-slate-400 to-slate-500", chip: "bg-rose-100 text-rose-700", msg: t("sc_retry"), Icon: RotateCcw },
  }[tier];

  return (
    <div className="relative mt-5 w-full flex flex-col items-center" data-testid={testid}>
      {burst && <FlameBurst />}

      <div className="flex items-center gap-2">
        <button onClick={() => playTTS(phrase)} data-testid={`${testid}-listen`}
          className="inline-flex items-center gap-2 bg-white/5 text-cyan-300 font-semibold px-4 py-2 rounded-full press text-sm"><Volume2 size={16} /> {t("listen")}</button>
      </div>

      {state === "listening" && <Waveform analyser={rec.analyser} active bars={24} tone="blue" className="w-full mt-4" />}
      {state === "scoring" && <p className="text-slate-400 flex items-center gap-2 mt-4 text-sm"><Loader2 className="animate-spin" size={16} /> {t("scoring_pron")}</p>}

      {state === "result" && result && (
        <div className={`mt-4 w-full flex flex-col items-center animate-pop ${tier === "retry" ? "animate-shake" : ""}`} data-testid={`${testid}-result`}>
          <div className="relative w-20 h-20">
            <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
              <path d="M18 2.5 a15.5 15.5 0 1 1 0 31 a15.5 15.5 0 1 1 0 -31" fill="none" stroke="rgba(15,23,42,0.08)" strokeWidth="3.5" />
              <path d="M18 2.5 a15.5 15.5 0 1 1 0 31 a15.5 15.5 0 1 1 0 -31" fill="none" stroke="url(#scg)" strokeWidth="3.5" strokeLinecap="round" strokeDasharray={`${score}, 100`} />
              <defs><linearGradient id="scg" x1="0" y1="0" x2="1" y2="1">
                <stop stopColor={tier === "fire" ? "#f97316" : tier === "good" ? "#10b981" : "#94a3b8"} />
                <stop offset="1" stopColor={tier === "fire" ? "#ef4444" : tier === "good" ? "#14b8a6" : "#64748b"} />
              </linearGradient></defs>
            </svg>
            <span className={`absolute inset-0 flex items-center justify-center ${tier === "fire" ? "fire-glow" : ""}`}>
              <tierStyle.Icon size={24} className={tier === "fire" ? "text-orange-500" : tier === "good" ? "text-emerald-500" : "text-slate-400"} />
            </span>
          </div>
          <span className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold text-sm ${tierStyle.chip}`} data-testid={`${testid}-verdict`}>
            {tierStyle.msg} · {score}%
          </span>
          {result.feedback && <p className="text-slate-500 text-sm text-center mt-2 max-w-[300px]">{result.feedback}</p>}
          {(result.words || []).length > 0 && (
            <div className="flex flex-wrap justify-center gap-1.5 mt-3">
              {result.words.map((w, i) => (
                <span key={i} className={`px-2 py-0.5 rounded-md border text-xs font-medium ${w.score >= 80 ? "bg-emerald-100 text-emerald-700 border-emerald-300" : w.score >= 55 ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-rose-100 text-rose-700 border-rose-300"}`}>{w.word}</span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-col items-center gap-2">
        <button onClick={toggle} disabled={state === "scoring"} data-testid={`${testid}-record`}
          className={`relative w-16 h-16 rounded-full flex items-center justify-center text-white press disabled:opacity-60 shadow-[0_12px_32px_-8px_rgba(37,99,235,0.6)] ${state === "listening" ? "bg-red-500" : tier === "fire" && state === "result" ? "bg-gradient-to-br from-orange-500 to-red-500" : "mel-gradient"}`}>
          {state === "listening" && <span className="absolute inset-0 rounded-full bg-red-400 mic-ring-out" />}
          {state === "result" ? <RotateCcw size={24} className="relative" /> : <Mic size={24} className="relative" />}
        </button>
        <p className="text-xs text-slate-400">{state === "listening" ? t("tap_to_stop") : state === "result" ? t("try_again_word") : t("sc_check")}</p>
      </div>
    </div>
  );
}
